/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef } from 'react';
import {
  CpdsDropDownStyleTag,
  CpdsDropDownButton,
  CpdsDropDownItems,
  CpdsDropDownItem,
  CpdsDropDownItemLeading,
  CpdsDropDownItemTrailing,
  CpdsDropDownItemImg,
  CpdsDropDownItemLink,
  DropdownPopupOptionList,
  DropdownPopupActionHeader,
  DropdownPopupCloseButton,
  DropdownPopupOptions
} from './styles/cpds-drop-down-style';
import { CpdsDropDownProps } from './typings';
import { themeStyleClass } from '../utils/cpds-utils';
import { checkValue } from 'src/components/common/helper';
import { Cpds } from '@Cpds.Ui';
import { css } from 'styled-components';

const CpdsDropDown: React.FC<CpdsDropDownProps> = (props) => {
  const { domRef, exClasses = '', styleOverrides, children, label, maxItemsToShow, options, ...rest } = props;
  const initValue = options.filter((option) => option.ItemText === label)?.[0]?.ItemValue;
  const dropDownOptionViewRef = useRef(null);
  const [hideDropdown, setHideDropdown] = React.useState(true);
  const [selectedValue, setSelectedvalue] = React.useState(initValue ?? '');

  const { themeColorMode, themeTypography } = rest;

  const className = themeStyleClass('cpds-drop-down-wrapper', themeTypography, exClasses);
  /* Important : By default html tag is <div></div> 
  but you can change that into suitable html element base on your requirements*/
  //const Tag: any = tag === 'button'? CpdsButton : CpdsDropDownStyleTag;

  //hide the dropdown when clicked outside
  useEffect(() => {
    // Click event handler
    function handleClickOutside(event) {
      if (dropDownOptionViewRef.current && !dropDownOptionViewRef.current.contains(event.target)) {
        setHideDropdown(true);
      }
    }
    // Add the click event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = (value) => {
    setHideDropdown(true);
    setSelectedvalue(value);
    props.onItemChange(value);
  }

  const handleDropDownClick = () => {
    setHideDropdown(!hideDropdown);
  };

  const renderLeadingComponent = (option: any, themeColorMode: string, index) => {
    if (typeof option.ItemLeading === 'string') {
      return (
        <CpdsDropDownItemImg className="cpds-drop-down-item-img" {...option} themeColorMode={themeColorMode}></CpdsDropDownItemImg>
      );
    } else {
      return (
        <Cpds.Icon id={`L-key-${index}`} name={option.ItemLeading.name || 'check'} size={option.ItemLeading.size || 'small'} />
      );
    }
  };

  const renderItems = (option: any, themeColorMode: string, index) => {
    return (
      <CpdsDropDownItem
        key={index}
        className="cpds-drop-down-item"
        themeColorMode={themeColorMode}
        selected={selectedValue === option.ItemValue}>
        {checkValue(option.ItemLeading) &&
          <CpdsDropDownItemLeading className="cpds-drop-down-item-leading">
            {renderLeadingComponent(option, themeColorMode, index)}
          </CpdsDropDownItemLeading>
        }
        <CpdsDropDownItemLink className="cpds-drop-down-item-link" onClick={() => handleClick(option.ItemValue)}>
          {option.ItemText}
        </CpdsDropDownItemLink>
        {checkValue(option.ItemTrailing) &&
          <CpdsDropDownItemTrailing className="cpds-drop-down-item-trailing">
            <Cpds.Icon
              id={`key-${index}`}
              name={option.ItemTrailing.name || 'chevron-right'}
              size={option.ItemTrailing.size || 'small'}
            />
          </CpdsDropDownItemTrailing>
        }
      </CpdsDropDownItem>
    );
  };

  return (
    <CpdsDropDownStyleTag
      className={`${className}`}
      ref={dropDownOptionViewRef}
      styleOverrides={styleOverrides}
      themeColorMode={themeColorMode}
      isOpen={!hideDropdown}
    >
      <CpdsDropDownButton
        className="cpds-drop-down-button"
        {...rest}
        disabled={rest.state === 'DISABLED' ? true : false}
        themeColorMode={themeColorMode}
        onClick={() => handleDropDownClick()}
        isOpen={!hideDropdown}
      >
        {label || 'Label'}
        <Cpds.Icon styleOverrides={css`margin-left: 4px`} id='dorpdown-icon' name='chevron-down' customSize={{ width: '10', height: '10' }} />
      </CpdsDropDownButton>
      {/* <Cpds.Button 
        exClasses='cpds-drop-down-button'
        {...rest} 
        disabled={rest.state === 'DISABLED' ? true : false}
        themeColorMode={themeColorMode} 
        tag='button' buttonType='Tertiary'>
        {label || 'Label'}
        <Cpds.Icon id='dorpdown-icon' name='chevron-down' />
      </Cpds.Button> */}
      {rest.state !== 'DISABLED' &&
        <Cpds.Viewport>
          <Cpds.ViewportMedium>
            <CpdsDropDownItems maxItemsToShow={maxItemsToShow} className="cpds-drop-down-items cpds-meta-roman-medium-book-09" themeColorMode={themeColorMode}>
              {maxItemsToShow > 0 && checkValue(maxItemsToShow) ?
                <Cpds.scrollBar>
                  {options.map((option, index) => (
                    renderItems(option, themeColorMode, index)
                  ))}
                </Cpds.scrollBar>
                :
                options.map((option, index) => (
                  renderItems(option, themeColorMode, index)
                ))
              }
            </CpdsDropDownItems>
          </Cpds.ViewportMedium>
          <Cpds.ViewportSmall>
            <DropdownPopupOptionList>
              <Cpds.scrollBar>
                <DropdownPopupActionHeader>
                  <DropdownPopupCloseButton onClick={() => handleDropDownClick()}>
                    <Cpds.Icon id="close-icon" name="close" />
                  </DropdownPopupCloseButton>
                </DropdownPopupActionHeader>
                <DropdownPopupOptions>
                  {options
                    .filter((option) => option?.ItemValue !== 'pick-a-date')
                    .map((option) => ({
                      ...option,
                      ...(selectedValue === option.ItemValue ? { ItemLeading: { name: 'check', size: 'small' } } : {}),
                    }))
                    .map((option, index) => renderItems(option, themeColorMode, index))}
                </DropdownPopupOptions>
              </Cpds.scrollBar>
            </DropdownPopupOptionList>
          </Cpds.ViewportSmall>
        </Cpds.Viewport>
      }
    </CpdsDropDownStyleTag>
  );
};

export default CpdsDropDown;

